import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Plus, X, Heart, ExternalLink } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function DonateAdmin() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    intro: "",
    qr_path: null,
    paypal_url: "",
    project_highlights: [],
    bank_info: "",
  });
  const [newProject, setNewProject] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/donations").then((r) => setForm({
      intro: r.data.intro || "",
      qr_path: r.data.qr_path || null,
      paypal_url: r.data.paypal_url || "",
      project_highlights: r.data.project_highlights || [],
      bank_info: r.data.bank_info || "",
    }));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/donations", form);
      toast.success(t("admin.donate.toastSaved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addProject = () => {
    const v = newProject.trim();
    if (!v) return;
    setForm((f) => ({ ...f, project_highlights: [...(f.project_highlights || []), v] }));
    setNewProject("");
  };

  const removeProject = (idx) => {
    setForm((f) => ({
      ...f,
      project_highlights: f.project_highlights.filter((_, i) => i !== idx),
    }));
  };

  return (
    <div data-testid="donate-admin" className="space-y-8 max-w-4xl">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-bordeaux-accent mb-2">{t("admin.donate.eyebrow")}</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t("admin.donate.title")}</h1>
          <p className="text-slate-600 mt-2">{t("admin.donate.subtitle")}</p>
        </div>
        <Link
          to="/spenden"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="open-public-donate"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-bordeaux border border-slate-300 hover:border-bordeaux rounded-sm px-3 py-2"
        >
          <ExternalLink size={14} /> {t("admin.donate.viewPublic")}
        </Link>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-6 md:p-8 space-y-6">
        <Field label={t("admin.donate.fieldIntro")}>
          <Textarea
            rows={4}
            value={form.intro}
            onChange={(e) => setForm({ ...form, intro: e.target.value })}
            placeholder={t("admin.donate.fieldIntroPlaceholder")}
            data-testid="donate-intro-input"
            className="rounded-sm border-slate-300"
          />
        </Field>

        <Field label={t("admin.donate.fieldQr")}>
          <div className="space-y-3">
            <ImageUploader
              value={form.qr_path}
              onChange={(p) => setForm({ ...form, qr_path: p })}
              testId="donate-qr-uploader"
              label={t("admin.donate.uploadQr")}
            />
            <p className="text-xs text-slate-500">{t("admin.donate.qrHint")}</p>
          </div>
        </Field>

        <Field label={t("admin.donate.fieldPaypal")}>
          <Input
            type="url"
            value={form.paypal_url}
            onChange={(e) => setForm({ ...form, paypal_url: e.target.value })}
            placeholder="https://paypal.me/your-handle"
            data-testid="donate-paypal-input"
            className="rounded-sm border-slate-300"
          />
        </Field>

        <Field label={t("admin.donate.fieldProjects")}>
          <div className="space-y-3">
            {(form.project_highlights || []).length > 0 && (
              <ul className="space-y-2">
                {form.project_highlights.map((p, i) => (
                  <li
                    key={i}
                    data-testid={`donate-project-${i}`}
                    className="flex items-center gap-3 bg-beige-light border border-slate-200 rounded-sm px-3 py-2"
                  >
                    <Heart size={12} className="text-bordeaux flex-shrink-0" fill="currentColor" />
                    <span className="text-sm text-slate-700 flex-1">{p}</span>
                    <button
                      type="button"
                      onClick={() => removeProject(i)}
                      data-testid={`donate-project-remove-${i}`}
                      className="p-1.5 hover:bg-red-50 rounded-sm text-slate-500 hover:text-red-600 transition-colors"
                    >
                      <X size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <Input
                value={newProject}
                onChange={(e) => setNewProject(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); addProject(); }
                }}
                placeholder={t("admin.donate.projectPlaceholder")}
                data-testid="donate-new-project-input"
                className="rounded-sm border-slate-300"
              />
              <Button
                type="button"
                onClick={addProject}
                data-testid="donate-add-project-btn"
                variant="outline"
                className="rounded-sm border-slate-300 hover:bg-beige-light hover:text-slate-900"
              >
                <Plus size={14} className="mr-1.5" /> {t("admin.donate.addProject")}
              </Button>
            </div>
          </div>
        </Field>

        <Field label={t("admin.donate.fieldBank")}>
          <Textarea
            rows={5}
            value={form.bank_info}
            onChange={(e) => setForm({ ...form, bank_info: e.target.value })}
            placeholder={t("admin.donate.fieldBankPlaceholder")}
            data-testid="donate-bank-input"
            className="rounded-sm border-slate-300 font-mono text-sm"
          />
        </Field>

        <div className="pt-4 border-t border-slate-100">
          <Button
            onClick={save}
            disabled={busy}
            data-testid="donate-save-btn"
            className="bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm"
          >
            <Save size={14} className="mr-2" /> {busy ? t("admin.common.saving") : t("admin.common.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{label}</Label>
      {children}
    </div>
  );
}
